import { useEffect, useMemo, useRef, useState } from 'react'

type Options = {
  open: boolean
  onClose: () => void
  enabled?: boolean
  /** close if pulled more than this many px */
  threshold?: number
  /** rubber-band strength: smaller = less resistance */
  rubber?: number
}

function rubberBand(distance: number, constant: number) {
  // distance >= 0
  return (distance * constant) / (distance + constant)
}

export function useDrawerSwipeToClose({
  open,
  onClose,
  enabled = true,
  threshold = 90,
  rubber = 110,
}: Options) {
  const [translateY, setTranslateY] = useState(0)
  const [dragging, setDragging] = useState(false)

  const startYRef = useRef<number | null>(null)
  const lastYRef = useRef<number | null>(null)
  const lastTRef = useRef<number | null>(null)
  const velocityRef = useRef(0)

  useEffect(() => {
    if (!open) {
      setTranslateY(0)
      setDragging(false)
      startYRef.current = null
      lastYRef.current = null
      lastTRef.current = null
      velocityRef.current = 0
    }
  }, [open])

  const style = useMemo(
    () => ({
      transform: `translateY(${translateY}px)`,
      transition: dragging ? 'none' : 'transform 160ms ease',
      willChange: 'transform',
    }),
    [translateY, dragging]
  )

  const onPointerDown = (e: React.PointerEvent) => {
    if (!enabled || !open) return
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)

    setDragging(true)
    startYRef.current = e.clientY
    lastYRef.current = e.clientY
    lastTRef.current = performance.now()
    velocityRef.current = 0
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!enabled || !dragging) return
    if (startYRef.current == null) return

    const y = e.clientY
    const dyRaw = y - startYRef.current
    const dy = Math.max(0, dyRaw)

    setTranslateY(rubberBand(dy, rubber))

    const now = performance.now()
    const lastY = lastYRef.current
    const lastT = lastTRef.current
    if (lastY != null && lastT != null) {
      const dt = Math.max(1, now - lastT)
      velocityRef.current = (y - lastY) / dt // px/ms
    }
    lastYRef.current = y
    lastTRef.current = now
  }

  const end = () => {
    if (!enabled || !dragging) return
    setDragging(false)

    const fastSwipe = velocityRef.current > 0.9 // ~900px/s
    const passed = translateY > threshold

    if (fastSwipe || passed) {
      setTranslateY(0)
      onClose()
    } else {
      setTranslateY(0)
    }

    startYRef.current = null
    lastYRef.current = null
    lastTRef.current = null
    velocityRef.current = 0
  }

  const handlers = useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp: end,
      onPointerCancel: end,
    }),
    [enabled, open, dragging, translateY, threshold, rubber]
  )

  return {
    style,
    dragging,
    handlers,
  }
}
